import { LANGUAGES, matchesFoldedSearch, resolveLabel, uiLanguage, } from "./valueList";
export const VALUE_STATUS_TABS = ["all", "essential", "displayed", "hidden"];
/**
 * In an OnDemand list an available system value stays out of every selection until the tenant
 * imports it. An essential one is always displayed: OPCAT never auto-imports it, so `isImported`
 * cannot decide it.
 */
function isOffered(value, availableDefault) {
    return availableDefault !== "OnDemand" || value.isImported || value.origin !== "SystemAvailable";
}
/** Selectable for new records: neither deprecated nor hidden, and offered. */
export function isDisplayed(value, availableDefault) {
    return !value.isDeprecated && !value.isHidden && isOffered(value, availableDefault);
}
/**
 * A deprecated value only shows under "all": it is neither displayed nor hidden by the tenant.
 * A value an OnDemand list does not offer yet counts as hidden: displaying it imports it.
 */
export function isInStatusTab(value, tab, availableDefault) {
    if (tab === "all")
        return true;
    if (value.isDeprecated)
        return false;
    switch (tab) {
        case "essential":
            return value.origin === "SystemEssential";
        case "displayed":
            return isDisplayed(value, availableDefault);
        case "hidden":
            return !isDisplayed(value, availableDefault);
    }
}
/** The values as they will be once the pending changes are saved; OPCAT imports what it displays. */
export function applyPendingVisibility(values, pending) {
    return values.map((value) => {
        const displayed = pending[value.internalCode];
        if (displayed === undefined)
            return value;
        return displayed ? { ...value, isHidden: false, isImported: true } : { ...value, isHidden: true };
    });
}
/** Values a user can pick: displayed, and not under a hidden parent. */
function countSelectable(values, availableDefault) {
    const hiddenParents = new Set(values.filter((v) => v.isHidden).map((v) => v.internalCode));
    return values.filter((v) => isDisplayed(v, availableDefault) &&
        (v.parentInternalCode === null || !hiddenParents.has(v.parentInternalCode))).length;
}
/**
 * Why the Display switch of `value` cannot move, or null. Mirrors OPCAT's refusals:
 * - hiding: an essential value, a category with an essential sub-value, or a value whose hiding
 *   would leave nothing selectable (a category takes its sub-values with it);
 * - displaying: a deprecated value, or a sub-value while its category is hidden.
 * A hidden value is otherwise never locked. OPCAT also refuses to hide the catalog's default value
 * or its category, which the front does not know; that refusal comes back on save.
 */
export function visibilityLock(value, values, availableDefault) {
    if (!isDisplayed(value, availableDefault)) {
        if (value.isDeprecated)
            return "deprecated";
        const parent = values.find((v) => v.internalCode === value.parentInternalCode);
        return parent?.isHidden ? "parentHidden" : null;
    }
    if (value.origin === "SystemEssential")
        return "essential";
    const hasEssentialChild = values.some((v) => v.parentInternalCode === value.internalCode && v.origin === "SystemEssential" && !v.isDeprecated);
    if (hasEssentialChild)
        return "essentialChild";
    const afterHiding = values.map((v) => (v.internalCode === value.internalCode ? { ...v, isHidden: true } : v));
    return countSelectable(values, availableDefault) > 0 && countSelectable(afterHiding, availableDefault) === 0
        ? "lastDisplayed"
        : null;
}
export function valueStatus(value, availableDefault) {
    if (value.isDeprecated)
        return "notOffered";
    if (value.isHidden)
        return "hidden";
    if (!isOffered(value, availableDefault))
        return "notOffered";
    return value.origin === "SystemEssential" ? "alwaysDisplayed" : "displayed";
}
/**
 * Manual lists keep the stored order. Alphabetical lists rank by display zone first, the same in
 * every language, then by the label the reader sees in the reader's locale.
 */
export function orderValues(values, sortMode, locale) {
    if (sortMode === "Manual") {
        return [...values].sort((a, b) => a.sortOrder - b.sortOrder || a.internalCode.localeCompare(b.internalCode));
    }
    const collator = new Intl.Collator(locale, { sensitivity: "base" });
    return [...values].sort((a, b) => a.displayZone - b.displayZone ||
        collator.compare(resolveLabel(a.labels, locale).text, resolveLabel(b.labels, locale).text) ||
        a.internalCode.localeCompare(b.internalCode));
}
/** Every language and both codes, whatever the UI language. */
export function matchesValueSearch(value, term) {
    return matchesFoldedSearch([...LANGUAGES.map((language) => value.labels[language]), value.internalCode, value.externalCode], term);
}
/** The language the identity column names in its header: the UI's, or English when the catalogue has none. */
export function identityLanguage(locale) {
    return uiLanguage(locale) ?? "en";
}
/** The languages shown beside the identity column: every catalogue language but the identity one. */
export function secondaryLanguages(locale) {
    const identity = identityLanguage(locale);
    return LANGUAGES.filter((language) => language !== identity);
}
